import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the Identity lifecycle screen (task 11.7; design 11, 12.2 Identity row).
 *
 * Three operator-API operations back the screen, all role `viewer`, taken straight from the
 * generated `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/identity/enrolment-funnel` → {@link EnrolmentFunnel}: the start→poll→activated→
 *   bound funnel with per-stage counts and conversion ratios, terminal failures, the `completed`
 *   time-to-complete statistics (which EXCLUDE stuck-pending enrolments by contract), the
 *   `non_converted` count (which INCLUDES stuck-pending), and the `stuck_pending` block (count,
 *   oldest age, and the per-entry current ages). This is the primary snapshot the screen rests on.
 * - `GET /api/v1/identity/tokens?step=…` → {@link TokenStats}: issuance/denial rows by kind,
 *   version, outcome and denial reason, plus an issued/denied over-time `series`.
 * - `GET /api/v1/identity/lifecycle` → {@link LifecyclePage}: one page of the revoke/suspend/
 *   deprovision/… timeline (newest first) WITH the current key and revocation epochs.
 *
 * Every window is resolved from the shared time-range exactly as the Overview/Decisions screens do,
 * so pausing freezes every query: in live mode "now" is bucketed to a 10s grid to keep the query key
 * stable between ticks; when frozen (paused or custom) `resolveWindow` returns the exact frozen
 * window and the key stops advancing (design 12.2, FR-13 AC2). Fetches go through `lib/api.ts`, so a
 * cold-core 503 surfaces as a `waking` {@link ApiError} and the shell shows "Waking the control
 * plane"; queries are retried per `query-client.ts`.
 */

/** The contract `EnrolmentFunnel` response body. */
export type EnrolmentFunnel = components["schemas"]["EnrolmentFunnel"];
/** The contract `TokenStats` response body. */
export type TokenStats = components["schemas"]["TokenStats"];
/** The contract `LifecyclePage` response body. */
export type LifecyclePage = components["schemas"]["LifecyclePage"];

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface IdentityWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseEnrolmentFunnelResult {
  readonly query: UseQueryResult<EnrolmentFunnel, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: IdentityWindow;
}

export interface UseIdentityTokensResult {
  readonly query: UseQueryResult<TokenStats, ApiError>;
  readonly window: IdentityWindow;
}

export interface UseIdentityLifecycleResult {
  readonly query: UseQueryResult<LifecyclePage, ApiError>;
  readonly window: IdentityWindow;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): IdentityWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

/**
 * Fetch the enrolment funnel for the current window. The query key carries the window so a frozen
 * window never refetches and a live window advances on the 10s grid; `staleTime`/`refetchInterval`
 * match the other screens (live tolerates a little staleness, frozen never goes stale).
 */
export function useEnrolmentFunnel(): UseEnrolmentFunnelResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<EnrolmentFunnel, ApiError>({
    queryKey: ["identity-funnel", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<EnrolmentFunnel>("/identity/enrolment-funnel", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch token issuance/denial stats for the current window. The over-time `series` is bucketed by
 * the operation's `step` — resolved from the window span so the chart has a sensible number of
 * buckets regardless of the preset — and `step` is part of the query key so it stays stable.
 */
export function useIdentityTokens(): UseIdentityTokensResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();
  const stepS = stepForWindow(window);

  const query = useQuery<TokenStats, ApiError>({
    queryKey: ["identity-tokens", window.fromMs, window.toMs, stepS],
    queryFn: ({ signal }) =>
      apiFetch<TokenStats>("/identity/tokens", {
        query: { from: window.fromMs, to: window.toMs, step: stepS },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch the first page of the lifecycle timeline for the current window, newest first, with the
 * current epochs. This screen shows the first page only (the newest events plus the epochs); the
 * `next_cursor` is left for a later pagination affordance. The window freezes with the others.
 */
export function useIdentityLifecycle(): UseIdentityLifecycleResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<LifecyclePage, ApiError>({
    queryKey: ["identity-lifecycle", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<LifecyclePage>("/identity/lifecycle", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * A sensible `step` (bucket size, seconds) for the token series given the window span, aiming for
 * roughly 60–120 buckets so the chart is dense but not noisy. Clamped to the contract-friendly
 * 10s..1h range. Kept here so it is part of the query key and therefore stable per window.
 */
function stepForWindow(window: IdentityWindow): number {
  const spanMs = Math.max(0, window.toMs - window.fromMs);
  const targetBuckets = 90;
  const rawStepS = spanMs / targetBuckets / 1000;
  const choices = [10, 30, 60, 300, 600, 1800, 3600];
  for (const choice of choices) {
    if (rawStepS <= choice) return choice;
  }
  return choices[choices.length - 1] as number;
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
